import { Avatar, Box, CardHeader, CircularProgress, Paper, Stack, Tooltip, Typography } from "@mui/material";
import DOMPurify from "dompurify";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";
import { useGetMessageCollectionDetail } from "../../App/Queries/MessageQueries";
import { useAppSelector } from "../../App/State/Hooks/reduxHooks";
import { selectClientValue } from "../../App/State/Slices/SelectedClientSlice";
import { useUserProfileStore } from "../../App/State/Stores/UserProfileStore";
import { IFeatureToggles } from "../../App/Typings/FeatureToggles";
import {
  ICandidateWithMessageInfo,
  IMessageDetail,
  IMessagePerformanceDto,
} from "../../App/Typings/MessageText";
import { candidateInitials } from "../../MessagingCenter/Utils";

export default function IndividualEmailCollectionDisplay({
  selectedCollection,
}: {
  selectedCollection: IMessagePerformanceDto | undefined;
}) {
  const { useCustomEmailDomain } = useFlags<IFeatureToggles>();
  const { getUserAdjustedDate, getUserAdjustedDateFromNow } = useUserProfileStore();
  const { clientName, clientEmailAddress, senderEmailDomain } = useAppSelector(selectClientValue);
  const [previewTemplateView, setPreviewTemplateView] = useState<IMessageDetail | null>(null);
  const previewCandidate: Partial<ICandidateWithMessageInfo> = {
    firstName: "Preview",
    lastName: "Candidate",
  };
  const { data, isLoading, isError } = useGetMessageCollectionDetail(
    selectedCollection?.messageCollectionId ?? "",
  );

  let fromAddress = clientEmailAddress;

  if (data?.sender) {
    fromAddress = data.sender;
  } else if (useCustomEmailDomain && senderEmailDomain) {
    fromAddress = `recruiter@${senderEmailDomain}`;
  }

  useEffect(() => {
    if (data) {
      setPreviewTemplateView({
        id: "",
        toAddress: `${previewCandidate.firstName?.toLowerCase()}@example.com`,
        toName: previewCandidate.firstName + " " + previewCandidate.lastName,
        fromAddress,
        fromName: clientName,
        message: data.message,
        subject: data.subject,
        messageType: "DirectEmailMessage",
        replyToMessageId: "",
        userHasReplyNotification: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromAddress, clientName, data]);
  return isLoading ? (
    <Stack alignItems="center" justifyContent="center">
      <CircularProgress size={20} color="inherit" />
    </Stack>
  ) : isError || !previewTemplateView ? (
    <Typography variant="body1">Error loading message</Typography>
  ) : (
    <>
      <>
        <Box sx={{ p: 1 }}>
          <Stack direction="row" justifyContent={"space-between"}>
            <Typography fontSize={18} data-test="message-view-title">
              {previewTemplateView.subject}
            </Typography>
            {previewTemplateView.sentDate && (
              <Typography fontSize={12} data-test="message-view-date">{`${getUserAdjustedDate(
                previewTemplateView.sentDate ?? new Date(),
              ).format("DD MMM YYYY, h:mm a")} (${getUserAdjustedDateFromNow(
                previewTemplateView.sentDate ?? new Date(),
              )})`}</Typography>
            )}
          </Stack>
        </Box>
        <Box>
          <Stack direction={"row"} justifyContent="space-between" alignItems={"center"}>
            <CardHeader
              data-test="message-view-email-info"
              avatar={
                <Avatar
                  sx={{ backgroundColor: "primary.main" }}
                  title={previewTemplateView.toName ?? previewTemplateView.toAddress ?? ""}
                >
                  {candidateInitials(previewCandidate)}
                </Avatar>
              }
              title={
                <Tooltip placement="top-start" title={<>{previewTemplateView.toAddress}</>}>
                  <Stack direction="row">
                    <Typography>to:&nbsp;</Typography>
                    <Typography fontWeight={700}>
                      {previewTemplateView.toName ?? previewTemplateView.toAddress}
                    </Typography>
                  </Stack>
                </Tooltip>
              }
              subheader={
                <Tooltip placement="top-start" title={<>{previewTemplateView.fromAddress}</>}>
                  <Stack direction="row">
                    <Typography>from:&nbsp;</Typography>
                    <Typography fontWeight={700}>{previewTemplateView.fromName}</Typography>
                  </Stack>
                </Tooltip>
              }
            />
          </Stack>
          <Paper sx={{ padding: 5 }} elevation={4}>
            <pre
              data-test="message-view-content-body"
              style={{ whiteSpace: "pre-wrap", wordWrap: "break-word", margin: 0 }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewTemplateView.message) }}
            ></pre>
          </Paper>
        </Box>
      </>
    </>
  );
}

DOMPurify.addHook("afterSanitizeAttributes", function (node) {
  if (node.nodeName === "A" && node.getAttribute("href")?.includes("managesubscription")) {
    node.setAttribute("href", "#");
  }
});
